/**
 * Removes undefined recursively. Firestore does not allow undefined anywhere in the object tree.
 *
 * Only recurses into plain object literals. Firestore's own special values — the
 * serverTimestamp()/increment()/arrayUnion()/deleteField() FieldValue sentinels, Timestamp,
 * GeoPoint, DocumentReference — and Date are class instances, not plain objects. Rebuilding one
 * via Object.entries() copies its enumerable own properties into a fresh `{}` and strips its
 * class identity, so the SDK no longer recognises it and writes it literally: serverTimestamp()
 * was being stored as the map `{_methodName: "serverTimestamp"}` instead of the write time
 * (employerWallets, publicProfiles, profileUnlocks, unlockedProfiles, organizations
 * hiringChecklist/onboardingTemplate). Class instances are passed through untouched; they never
 * contain an `undefined` this function needs to strip.
 *
 * This is the one implementation — do not add local copies (two did exist, with the same bug).
 */
export function sanitizeForFirestore<T>(value: T): T {
  if (value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => sanitizeForFirestore(v)).filter((v) => v !== undefined) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) {
      return value;
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      const sv = sanitizeForFirestore(v);
      if (sv === undefined) continue;
      out[k] = sv;
    }
    return out as T;
  }
  return value;
}
